import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import ProgressStepper from '../ProgressStepper'
import Step0 from '../steps/Step0.jsx'
import Step4 from '../steps/Step4.jsx'
import QuizCharacter from './QuizCharacter.jsx'
import QuizFinal from './QuizFinal.jsx'
import AuthModal from '../shared/AuthModal'
import LogoSvg from '../../assets/Logo.svg'
import { QUIZ_ROUTE_STEPS, STORAGE_KEYS } from '../../constants'
import { useQuizSelections } from '../../hooks/useLocalStorage'
import { initTracking } from '../../utils/tracking'
import { setAuth } from '../../utils/storage'
import { trackRegistrationCompleted } from '../../utils/analytics'

const STEPS_COMPONENTS = {
  welcome: Step0,
  character: QuizCharacter,
  step4: Step4,
  step5: QuizFinal,
}

const validateStepData = (step, answer) => {
  switch (step) {
    case 'welcome':
      return answer && typeof answer === 'string' && answer.trim() !== ''
    case 'character':
      return answer && answer.value
    case 'step4':
      return answer && answer.personality && answer.personalityTrait
    default:
      return true
  }
}

const QuizFlow = () => {
  const [currentStep, setCurrentStep] = useState(0)
  const [displayedStep, setDisplayedStep] = useState(0)
  const [transitionState, setTransitionState] = useState('idle')
  const [showAuthModal, setShowAuthModal] = useState(false)

  const location = useLocation()
  const [selections, setSelections] = useQuizSelections(location.pathname)
  const pendingStepRef = useRef(null)
  const activeSteps = QUIZ_ROUTE_STEPS
  const isQuizSfw = location.pathname.includes('/quiz-sfw')

  useEffect(() => {
    document.body.classList.add('quiz-mode')
    document.body.classList.add('ndg-mode')
    return () => {
      document.body.classList.remove('quiz-mode')
      document.body.classList.remove('ndg-mode')
    }
  }, [])

  useEffect(() => {
    initTracking()
  }, [])

  // /quiz-sfw: hard auth gate on entry — open modal immediately (not after Step0)
  // and make it non-closable. /quiz keeps the after-welcome trigger via handleAnswer.
  useEffect(() => {
    if (!isQuizSfw) return
    if (sessionStorage.getItem('quizSessionAuthed') === 'true') return
    setShowAuthModal(true)
  }, [isQuizSfw])

  // Resume the quiz after Google OAuth redirects back with ?auth_token=...
  // (matches the manual-signup behaviour in createAccount.jsx — save token, mark session
  // authed, jump past the welcome step, then strip the auth params from the URL.)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const authToken = params.get('auth_token')
    if (!authToken) return

    localStorage.setItem('quizAuthToken', authToken)
    sessionStorage.setItem('quizSessionAuthed', 'true')
    setAuth(authToken)

    if (localStorage.getItem('pendingRegistrationSource') === 'google') {
      try { trackRegistrationCompleted('google') } catch {}
      localStorage.removeItem('pendingRegistrationSource')
    }

    const stored = JSON.parse(localStorage.getItem(STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS) || '{}')
    const resumeStep = stored?.welcome ? 1 : 0
    setCurrentStep(resumeStep)
    setDisplayedStep(resumeStep)

    const url = new URL(window.location.href)
    ;['auth_token', 'auth_type', 'model-params', 'type'].forEach(k => url.searchParams.delete(k))
    window.history.replaceState({}, '', url.toString())
  }, [])

  const transitionToStep = useCallback(
    newStep => {
      if (transitionState !== 'idle') return
      pendingStepRef.current = newStep
      setTransitionState('fade-out')
    },
    [transitionState],
  )

  const handleAnswer = useCallback(
    (step, answer) => {
      if (!validateStepData(step, answer)) return

      const existing = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS) || '{}',
      )
      let updated = { ...existing, [step]: answer }

      // Character selection encodes body attributes — propagate to step2 so
      // they're sent to the API in the standard format (bodyType / breastSize / buttSize).
      if (step === 'character' && answer && (answer.bodyType || answer.breastSize || answer.buttSize)) {
        updated = {
          ...updated,
          step2: {
            ...(existing.step2 || {}),
            bodyType: answer.bodyType,
            breastSize: answer.breastSize,
            buttSize: answer.buttSize,
          },
        }
      }

      localStorage.setItem(STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS, JSON.stringify(updated))
      setSelections(updated)

      if (step === 'welcome' && !isQuizSfw) {
        // /quiz: open the auth modal AFTER the user picks a style. /quiz-sfw is gated
        // on entry (separate useEffect above), so by the time we get here auth is done.
        const alreadyAuthed = sessionStorage.getItem('quizSessionAuthed') === 'true'
        if (!alreadyAuthed) {
          setShowAuthModal(true)
          return
        }
      }

      transitionToStep(currentStep + 1)
    },
    [setSelections, currentStep, transitionToStep, isQuizSfw],
  )

  const handlePrevious = useCallback(() => {
    if (transitionState !== 'idle' || currentStep === 0) return
    transitionToStep(currentStep - 1)
  }, [transitionState, currentStep, transitionToStep])

  const handleAuthSuccess = useCallback(() => {
    setShowAuthModal(false)
    // /quiz-sfw shows the modal on entry, so the user is still on the welcome step
    // and needs to pick a style — don't advance. /quiz shows the modal after the
    // welcome answer was already saved, so advancing is correct there.
    if (!isQuizSfw) {
      transitionToStep(currentStep + 1)
    }
  }, [currentStep, transitionToStep, isQuizSfw])

  const handleAuthClose = useCallback(() => {
    if (isQuizSfw) return
    setShowAuthModal(false)
  }, [isQuizSfw])

  useEffect(() => {
    if (transitionState === 'fade-out') {
      const timer = setTimeout(() => {
        setCurrentStep(pendingStepRef.current)
        setDisplayedStep(pendingStepRef.current)
        setTransitionState('fade-in')
      }, 300)
      return () => clearTimeout(timer)
    }
    if (transitionState === 'fade-in') {
      const timer = setTimeout(() => setTransitionState('idle'), 400)
      return () => clearTimeout(timer)
    }
  }, [transitionState])

  const totalVisibleSteps = useMemo(() => activeSteps.length, [activeSteps])
  const currentStepName = activeSteps[displayedStep]
  const CurrentStepComponent = STEPS_COMPONENTS[currentStepName]
  const isTransitioning = transitionState !== 'idle'
  const selectedStyle = selections?.welcome || 'Realistic'

  if (!CurrentStepComponent) {
    return null
  }

  return (
    <div className={`step-wrapper ${
      transitionState === 'fade-out'
        ? 'fade-out'
        : transitionState === 'fade-in'
        ? 'fade-in'
        : ''
    }`}>
      <header className='quiz-header'>
        <img src={LogoSvg} alt='Swipey' className='quiz-header-logo' width={100} height={24} />
      </header>
      {currentStepName !== 'step5' && (
        <ProgressStepper currentStep={currentStep} totalSteps={totalVisibleSteps} />
      )}
      <CurrentStepComponent
        onAnswer={handleAnswer}
        selections={selections}
        onPrevious={handlePrevious}
        isTransitioning={isTransitioning}
      />
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={handleAuthClose}
          selectedStyle={selectedStyle}
          hideBackgroundVideo
          hideCloseButton={isQuizSfw}
          onSuccessContinue={handleAuthSuccess}
        />
      )}
    </div>
  )
}

export default QuizFlow
